import TwitterIcon from "@assets/icons/twitter.svg";
import Layout from "components/layout";
import { AppHeader } from "components/layout/Header";
import { LuckContents } from "features/daily-holoscope/components/LuckContents";
import { GetServerSideProps, GetServerSidePropsContext } from "next";
import { NextSeo, NextSeoProps } from "next-seo";
import { useState, ReactNode } from "react";
import { Avatar } from "shared/components/atoms/Avatar";
import { Button } from "shared/components/atoms/Button";
import { Image } from "shared/components/atoms/Image";
import { ScrollMeasure } from "shared/components/atoms/ScrollMeasure";
import DailyHoloscope from "shared/models/daily_holoscope";
import clients from "shared/repositories/HttpClient";
import { handleServerSideError } from "shared/utils/ssr";
import { windowOpen } from "shared/utils/windowOpen";
import { MetaTag } from "utils/seo";
import getNextSeoProps from "utils/ssr/getNextSeoProps";

const headerTransitionPoint = 140;

interface Props {
  dailyHoloscope?: any;
  nextSeoProps?: NextSeoProps;
}

const DailyHoloscopeDetail = ({ nextSeoProps, ...rest }: Props) => {
  const [transparent, setTransparent] = useState(true);

  const onScroll = (
    _clientHeight: number,
    _innerHeight: number,
    scrollY: number
  ) => {
    if (transparent && scrollY >= headerTransitionPoint) {
      setTransparent(false);
    } else if (!transparent && scrollY < headerTransitionPoint) {
      setTransparent(true);
    }
  };

  const dailyHoloscope = new DailyHoloscope(rest.dailyHoloscope);
  const { zodiacSign, period } = dailyHoloscope;

  return (
    <>
      <NextSeo {...nextSeoProps} />
      <ScrollMeasure onScroll={onScroll}>
        <div tw="relative">
          <Image
            src={`${process.env.NEXT_PUBLIC_CDN_V2}/decoration/symmetry_rainbow.png`}
            width={375}
            height={140}
            layout="responsive"
            priority
          />
          <div tw="absolute top-0 w-full">
            <AppHeader transparent={transparent} />
            <div tw="pt-14 flex-center flex-col">
              <Avatar
                tw="mt-10"
                src={`/icons/${zodiacSign.rawValue}.svg`}
                size={85}
              />
            </div>
          </div>
        </div>
        <div tw="mt-14 px-4">
          <h1 tw="font-semibold text-lg leading-7 text-center text-[#3c424a] tracking-[.53px]">
            {zodiacSign.value}の今日の運勢
          </h1>
          <div tw="font-hira-mincho font-light text-base leading-7 text-center mt-1 text-[#E271B6] tracking-[.47px]">
            {period}
          </div>
          <div tw="pt-[30px] px-1">
            <LuckContents dailyHoloscope={dailyHoloscope} />
          </div>
          <Button
            tw="bg-twitter px-2 my-14 whitespace-nowrap w-full"
            onClick={() => windowOpen(`twitter${zodiacSign.rawValue}`)}
          >
            <TwitterIcon tw="mr-2" />
            {zodiacSign.value}の今日の運勢を毎日Check!!
          </Button>
        </div>
      </ScrollMeasure>
    </>
  );
};

DailyHoloscopeDetail.getLayout = (page: ReactNode) => {
  return (
    <Layout header={null} nav={null}>
      {page}
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  ctx: GetServerSidePropsContext
) => {
  const { zodiacSign } = ctx.params;

  try {
    const res = await clients.miror.get(`/daily_holoscopes/${zodiacSign}`);
    const { dailyHoloscope } = res.data;
    const localizedSign = DailyHoloscope.localizeZodiacSign(
      dailyHoloscope.zodiacSign
    );

    const nextSeoProps = getNextSeoProps(ctx, {
      title: MetaTag.dailyHoloscopesZodiacSign.title(localizedSign),
      description: MetaTag.dailyHoloscopesZodiacSign.description(localizedSign),
      keywords: MetaTag.dailyHoloscopesZodiacSign.keywords(localizedSign),
      url: `https://miror.jp/daily_holoscope/${dailyHoloscope.zodiacSign}`,
    });

    return {
      props: {
        dailyHoloscope,
        nextSeoProps,
      },
    };
  } catch (error) {
    return handleServerSideError(error);
  }
};

export default DailyHoloscopeDetail;
